import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  App,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { styled } from 'styled-components';
import { useTranslate } from '@/translations';
import {
  Customer,
  Location,
  ShippingReferenceInput,
  VesselPortCall,
  VesselPositions,
} from '@/types';
import { DragUpload, TooltipIcon } from '@/ui';
import {
  REFERENCE_TYPES,
  vesselSelectOptions,
  vinRangeFromList,
} from './manifestEditor';
import { ReferenceImportModal } from './ReferenceImportModal';
import { VesselJourneyField } from './VesselJourneyField';
import {
  applyImportedReferences,
  ImportedReference,
  parseVinList,
} from './referenceImport';

export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export type ManifestFormValues = {
  manifestRef?: string;
  eta?: dayjs.Dayjs;
  vesselId?: string;
  vesselJourneyId?: number;
  movementId?: number;
  customerId?: number;
  siteId?: number;
  notes?: string;
};

export type ReferenceFormValues = {
  referenceType?: string;
  reference: string;
};

export type DraftReference = ShippingReferenceInput & {
  key: string;
  id?: number;
  vins: string[];
};

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
`;

const pendingMenu = (pending?: boolean) =>
  pending ? <Spin size="small" /> : undefined;

const locationServesCustomer = (location: Location, customerId?: number) =>
  customerId != null &&
  (location.customerIds?.includes(customerId) ||
    location.customers?.some((customer) => customer.value === customerId));

export const emptyReference = (): DraftReference => ({
  key: `new-${Date.now()}`,
  referenceType: 'BillOfLading',
  reference: '',
  vehicleCount: 0,
  startVin: '',
  vins: [],
});

type ManifestOverviewFieldsProps = {
  form: FormInstance<ManifestFormValues>;
  variant: 'create' | 'edit';
  positions: VesselPositions[];
  customers: Customer[];
  locations: Location[];
  portCalls?: VesselPortCall[];
  vesselsLoading?: boolean;
  portCallsLoading?: boolean;
  customersLoading?: boolean;
  locationsLoading?: boolean;
};

export const ManifestOverviewFields = ({
  form,
  variant,
  positions,
  customers,
  locations,
  portCalls = [],
  vesselsLoading,
  portCallsLoading,
  customersLoading,
  locationsLoading,
}: ManifestOverviewFieldsProps) => {
  const { t } = useTranslate();
  const vesselId = Form.useWatch('vesselId', form);
  const movementId = Form.useWatch('movementId', form);
  const customerId = Form.useWatch('customerId', form);
  const selectedVesselId = positions.find(
    (vessel) => String(vessel.vesselId ?? '') === String(vesselId),
  )?.vesselId;
  const visiblePortCalls = selectedVesselId
    ? portCalls.filter((item) => item.vesselId === selectedVesselId)
    : portCalls;
  const siteLoading = customerId != null && Boolean(locationsLoading);

  return (
    <OverviewGrid>
      <Form.Item
        name="manifestRef"
        label={t('manifestRef')}
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="eta" label={t('etaUtc')} rules={[{ required: true }]}>
        <DatePicker
          showTime
          style={{ width: '100%' }}
          format="DD/MM/YYYY HH:mm"
        />
      </Form.Item>
      <Form.Item
        name="vesselId"
        label={t('vessel')}
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          options={vesselSelectOptions(positions)}
          loading={vesselsLoading}
          notFoundContent={pendingMenu(vesselsLoading)}
          disabled={Boolean(movementId)}
          onChange={(next) => {
            form.setFieldValue('vesselId', next);
            form.setFieldValue('vesselJourneyId', undefined);
          }}
        />
      </Form.Item>
      <Form.Item name="movementId" label={t('relatedPortCall')}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          loading={portCallsLoading}
          notFoundContent={pendingMenu(portCallsLoading)}
          options={visiblePortCalls.map((item) => ({
            value: item.id,
            label: `${item.portCallRef || item.id} · ${item.vesselName || ''} · ${item.voyage || ''}`,
          }))}
          onChange={(next) => {
            const selected = visiblePortCalls.find((item) => item.id === next);
            form.setFieldValue('movementId', next);
            if (!selected) return;
            form.setFieldValue('vesselId', String(selected.vesselId));
            form.setFieldValue('vesselJourneyId', selected.vesselJourneyId);
            if (selected.siteId) form.setFieldValue('siteId', selected.siteId);
          }}
        />
      </Form.Item>
      <Form.Item
        name="vesselJourneyId"
        label={t('vesselJourney')}
        rules={[{ required: true }]}
      >
        <VesselJourneyField
          vesselId={selectedVesselId}
          disabled={Boolean(movementId)}
        />
      </Form.Item>
      <Form.Item
        name="customerId"
        label={t('customer')}
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          loading={customersLoading}
          notFoundContent={pendingMenu(customersLoading)}
          options={customers.map((item) => ({
            value: item.customerId,
            label: item.customerName,
          }))}
          onChange={(next) => {
            form.setFieldValue('customerId', next);
            const siteId = form.getFieldValue('siteId') as number | undefined;
            const stillValid = locations.some(
              (location) =>
                location.id === siteId &&
                locationServesCustomer(location, next),
            );
            if (!stillValid) form.setFieldValue('siteId', undefined);
          }}
        />
      </Form.Item>

      <Form.Item
        name="siteId"
        label={t('siteTerminal')}
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          loading={siteLoading}
          notFoundContent={pendingMenu(siteLoading)}
          disabled={customerId == null}
          options={locations
            .filter((location) => locationServesCustomer(location, customerId))
            .map((location) => ({
              value: location.id,
              label: location.desc || location.code,
            }))}
        />
      </Form.Item>
      <Form.Item name="notes" label={t('notes')}>
        <Input.TextArea
          rows={3}
          placeholder={variant === 'create' ? t('addManifestNotes') : undefined}
        />
      </Form.Item>
    </OverviewGrid>
  );
};

type ManifestReferenceTableProps = {
  references: DraftReference[];
  onPatch: (key: string, patch: Partial<DraftReference>) => void;
  onEdit: (reference: DraftReference) => void;
  onDelete: (reference: DraftReference) => void;
  confirmDelete?: boolean;
};

export const ManifestReferenceTable = ({
  references,
  onPatch,
  onEdit,
  onDelete,
  confirmDelete,
}: ManifestReferenceTableProps) => {
  const { t } = useTranslate();
  return (
    <Table
      size="small"
      rowKey="key"
      pagination={false}
      dataSource={references}
      columns={[
        {
          title: t('referenceType'),
          width: 170,
          render: (_, record: DraftReference) => (
            <Select
              allowClear
              style={{ width: '100%' }}
              value={record.referenceType || undefined}
              options={REFERENCE_TYPES}
              onChange={(referenceType) =>
                onPatch(record.key, { referenceType })
              }
            />
          ),
        },
        { title: t('shippingReference'), dataIndex: 'reference' },
        {
          title: t('quantity'),
          width: 110,
          render: (_, record: DraftReference) => record.vins.length,
        },
        {
          title: t('vinRange'),
          render: (_, record: DraftReference) => vinRangeFromList(record.vins),
        },
        {
          title: t('actions'),
          width: 120,
          align: 'center',
          render: (_, record: DraftReference) => (
            <>
              <TooltipIcon
                title={t('edit')}
                icon={faPenToSquare}
                onClick={() => onEdit(record)}
              />
              <TooltipIcon
                title={t('delete')}
                icon={faTrash}
                color="#ff4d4f"
                onClick={() => {
                  if (!confirmDelete) {
                    onDelete(record);
                    return;
                  }
                  Modal.confirm({
                    title: t('delete'),
                    content: record.reference,
                    onOk: () => onDelete(record),
                  });
                }}
              />
            </>
          ),
        },
      ]}
    />
  );
};

export const useManifestReferenceEditor = (
  setReferences: React.Dispatch<React.SetStateAction<DraftReference[]>>,
) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [referenceForm] = Form.useForm<ReferenceFormValues>();
  const [referenceDraft, setReferenceDraft] = useState<DraftReference>();
  const [importOpen, setImportOpen] = useState(false);
  const [importMultiple, setImportMultiple] = useState(true);
  const [importFile, setImportFile] = useState<File>();
  const [vehiclesOpen, setVehiclesOpen] = useState(false);

  useEffect(() => {
    if (!referenceDraft) referenceForm.resetFields();
  }, [referenceDraft, referenceForm]);

  const openReference = (next: DraftReference) => {
    referenceForm.setFieldsValue({
      referenceType: next.referenceType,
      reference: next.reference,
    });
    setReferenceDraft(next);
  };

  const saveReference = (values: ReferenceFormValues) => {
    if (!referenceDraft) return;
    const next = {
      ...referenceDraft,
      referenceType: values.referenceType,
      reference: values.reference.trim(),
    };
    setReferences((current) => {
      const exists = current.some((item) => item.key === next.key);
      if (!exists) return [...current, next];
      return current.map((item) => (item.key === next.key ? next : item));
    });
    setReferenceDraft(undefined);
  };

  const patchReference = (key: string, patch: Partial<DraftReference>) => {
    setReferences((current) =>
      current.map((item) => (item.key === key ? { ...item, ...patch } : item)),
    );
  };

  const removeReference = (reference: DraftReference) => {
    setReferences((current) =>
      current.filter((item) => item.key !== reference.key),
    );
  };

  const attachVinList = async (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      message.error(t('fileIsTooLarge'));
      return;
    }
    const vins = await parseVinList(file);
    if (!vins.length) {
      message.error(t('vinListNotRecognised'));
      return;
    }
    setReferenceDraft((current) =>
      current
        ? {
            ...current,
            vins,
            vehicleCount: vins.length,
            startVin: vins[0] ?? '',
          }
        : current,
    );
  };

  const openImport = (multiple: boolean, file?: File) => {
    setImportMultiple(multiple);
    setImportFile(file);
    setImportOpen(true);
  };

  const importReferences = (imported: ImportedReference[]) => {
    setReferences((current) =>
      applyImportedReferences(current, imported, (item) => ({
        key: `new-${Date.now()}-${item.reference}`,
        reference: item.reference,
        referenceType: item.referenceType,
        vehicleCount: item.vins.length,
        startVin: item.vins[0] ?? '',
        vins: item.vins,
      })),
    );
    setImportOpen(false);
    setImportFile(undefined);
    setReferenceDraft(undefined);
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportFile(undefined);
  };

  return {
    referenceForm,
    referenceDraft,
    setReferenceDraft,
    openReference,
    saveReference,
    patchReference,
    removeReference,
    attachVinList,
    openImport,
    importReferences,
    importOpen,
    importMultiple,
    importFile,
    closeImport,
    vehiclesOpen,
    setVehiclesOpen,
  };
};

export const ManifestReferenceDialogs = ({
  references,
  editor,
}: {
  references: DraftReference[];
  editor: ReturnType<typeof useManifestReferenceEditor>;
}) => {
  const { t } = useTranslate();
  return (
    <>
      <Modal
        title={editor.referenceDraft?.id ? t('edit') : t('addReference')}
        open={Boolean(editor.referenceDraft)}
        onCancel={() => editor.setReferenceDraft(undefined)}
        okText={t('saveChanges')}
        onOk={() => editor.referenceForm.submit()}
        destroyOnHidden
      >
        {editor.referenceDraft ? (
          <Form
            form={editor.referenceForm}
            layout="vertical"
            onFinish={editor.saveReference}
          >
            <Form.Item name="referenceType" label={t('referenceType')}>
              <Select options={REFERENCE_TYPES} />
            </Form.Item>
            <Form.Item
              name="reference"
              label={t('referenceNo')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t('quantity')}>
              <Input
                disabled
                value={String(editor.referenceDraft.vins.length)}
              />
            </Form.Item>
            <Form.Item label={t('vinRange')}>
              <Input
                disabled
                value={vinRangeFromList(editor.referenceDraft.vins)}
              />
            </Form.Item>
            <DragUpload
              multiple={false}
              accept=".csv,.xlsx,.xls"
              showUploadBtn={false}
              uploadText={t('importVinList')}
              onFileSelected={(next) => void editor.attachVinList(next)}
            />
          </Form>
        ) : null}
      </Modal>
      <Modal
        title={t('viewVehicles')}
        open={editor.vehiclesOpen}
        width={720}
        footer={null}
        onCancel={() => editor.setVehiclesOpen(false)}
      >
        {references
          .filter((item) => item.vins.length)
          .map((item) => (
            <section key={item.key} style={{ marginBottom: 16 }}>
              <strong>
                {`${item.reference || '—'} · ${item.vins.length} · ${vinRangeFromList(item.vins)}`}
              </strong>
              <Typography.Paragraph
                ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
                style={{ color: '#667085' }}
              >
                {item.vins.join(', ')}
              </Typography.Paragraph>
            </section>
          ))}
      </Modal>
      <ReferenceImportModal
        open={editor.importOpen}
        multiple={editor.importMultiple}
        file={editor.importFile}
        onCancel={editor.closeImport}
        onImport={editor.importReferences}
      />
    </>
  );
};
